/**
 * Environment readers. Surfaces a typed shape over `import.meta.env` so the
 * rest of the app touches `env.*` and never raw `import.meta.env`.
 *
 * The two Supabase variables are required for any real backend call. The
 * shell tolerates them being unset at module-load time so the dev server
 * can still serve the bundle — the failure surfaces as an envelope-aware
 * error state on the first API request instead of a hard crash.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
const FUNCTIONS_BASE_URL_OVERRIDE = import.meta.env.VITE_FUNCTIONS_BASE_URL ?? '';

// ── Task 141 (IC-3) — paid-media OAuth UI build-time gate ────────────
//
// `VITE_IC3_META_OAUTH_UI_ENABLED` controls whether the client paid-
// media tab renders the "اربط حساب إعلانات Meta" connect CTA AND
// whether the callback page at `/auth/integration-oauth-callback`
// has any visible production entry point. Default: OFF.
//
// Why OFF by default: the IC-3 EFs (Task 139) currently read the
// shared `META_REDIRECT_URI` env which points at the publishing
// callback. The IC-3 FE callback path is a DIFFERENT URL
// (/auth/integration-oauth-callback). Until the operator does the
// follow-up (separate task) — add a paid-media-specific redirect URI
// env on the EF side, redeploy the IC-3 EFs, register the new URL in
// the Meta App's allowed OAuth redirect URIs — the FE-to-Meta-to-FE
// loop will fail with a Meta-side `redirect_uri` mismatch and the
// user lands on a Meta error page. Rendering the CTA before that's
// done would create a broken production UX.
//
// Strict-true parsing: the flag is only true when the env var is
// exactly the string `'true'`. Any other value (unset, '', '0',
// 'false', 'TRUE', '1') is false. Aligns with the Vite convention
// of string envs + intentionally rejects sloppy values.
const IC3_META_OAUTH_UI_ENABLED = (import.meta.env.VITE_IC3_META_OAUTH_UI_ENABLED ?? '') === 'true';

// ── Task 190 (IC-6) — Snapchat paid-media OAuth UI build-time gate ────
//
// `VITE_IC6_SNAPCHAT_OAUTH_UI_ENABLED` controls whether the client paid-
// media tab renders the "اربط حساب إعلانات Snapchat" connect CTA.
// Default: OFF — same posture as the Meta flag. Before flipping to true in
// production the operator must: register the Snapchat redirect URI
// (`SNAPCHAT_PAID_MEDIA_REDIRECT_URI`, pointing at
// `/auth/integration-oauth-callback`) in the Snapchat app console, set
// `SNAPCHAT_CLIENT_ID` / `SNAPCHAT_CLIENT_SECRET` /
// `SNAPCHAT_PAID_MEDIA_REDIRECT_URI` on the EF deploy, and redeploy the
// integration OAuth EFs. Until then the FE-to-Snapchat-to-FE loop would
// fail with a redirect mismatch.
//
// Strict-true parsing — only the exact string `'true'` enables it.
const IC6_SNAPCHAT_OAUTH_UI_ENABLED =
  (import.meta.env.VITE_IC6_SNAPCHAT_OAUTH_UI_ENABLED ?? '') === 'true';

// ── Task 204 (IC-8) — TikTok paid-media OAuth UI build-time gate ──────
//
// `VITE_IC8_TIKTOK_OAUTH_UI_ENABLED` controls whether the client paid-
// media tab renders the "اربط حساب إعلانات TikTok" connect CTA.
// Default: OFF — same posture as the Meta/Snapchat flags. Before flipping
// to true in production the operator must: register the TikTok Marketing
// API app + the paid-media redirect URI (pointing at
// `/auth/integration-oauth-callback`) in the TikTok developer console, set
// the TikTok client id / secret / redirect-URI secrets on the EF deploy,
// apply the IC-8 migration, and deploy the integration OAuth EFs. Until
// then the FE-to-TikTok-to-FE loop would fail with a redirect mismatch.
//
// Strict-true parsing — only the exact string `'true'` enables it.
const IC8_TIKTOK_OAUTH_UI_ENABLED =
  (import.meta.env.VITE_IC8_TIKTOK_OAUTH_UI_ENABLED ?? '') === 'true';

// ── Task 195 (IC-7.4) — Snapchat MANUAL-SYNC UI build-time gate ───────
//
// `VITE_IC7_SNAPCHAT_SYNC_UI_ENABLED` controls whether the client paid-
// media tab renders the Snapchat manual-SYNC card/button. Default: OFF —
// so the merged FE stays INERT in production until backend activation is
// complete (IC-7.1 migration applied; the Snapchat Worker deployed with
// `SNAPCHAT_CLIENT_ID`/`SNAPCHAT_CLIENT_SECRET`; the
// `sync-paid-media-snapchat-campaigns` EF deployed;
// `KMI_PAID_MEDIA_WORKER_URL`/`_BEARER` confirmed on the EF runtime).
// Flipping it on before that would render a sync button that fails on click.
//
// This flag is INDEPENDENT of the IC-6 OAuth/connect flag: the Snapchat
// connect card + connected-account visibility stay rendered regardless;
// ONLY the sync trigger is gated by this flag.
//
// Strict-true parsing — only the exact string `'true'` enables it.
const IC7_SNAPCHAT_SYNC_UI_ENABLED =
  (import.meta.env.VITE_IC7_SNAPCHAT_SYNC_UI_ENABLED ?? '') === 'true';

function deriveFunctionsBaseUrl(): string {
  if (FUNCTIONS_BASE_URL_OVERRIDE.length > 0) return FUNCTIONS_BASE_URL_OVERRIDE;
  if (SUPABASE_URL.length > 0) return `${SUPABASE_URL.replace(/\/+$/, '')}/functions/v1`;
  return '';
}

export const env = {
  supabaseUrl: SUPABASE_URL,
  supabaseAnonKey: SUPABASE_ANON_KEY,
  functionsBaseUrl: deriveFunctionsBaseUrl(),
  isConfigured: SUPABASE_URL.length > 0 && SUPABASE_ANON_KEY.length > 0,
  /**
   * Task 141 — IC-3 paid-media OAuth UI build-time gate. Default OFF.
   * See comment block above for the operator follow-up required before
   * flipping this to `true` in production.
   */
  ic3MetaOauthUiEnabled: IC3_META_OAUTH_UI_ENABLED,
  /**
   * Task 190 — IC-6 Snapchat paid-media OAuth UI build-time gate.
   * Default OFF. See comment block above for the operator follow-up.
   */
  ic6SnapchatOauthUiEnabled: IC6_SNAPCHAT_OAUTH_UI_ENABLED,
  /**
   * Task 204 — IC-8 TikTok paid-media OAuth UI build-time gate.
   * Default OFF. See comment block above for the operator follow-up
   * (register the TikTok Marketing API app + redirect URI + secrets,
   * apply the migration, deploy the EFs) required before flipping it on.
   */
  ic8TikTokOauthUiEnabled: IC8_TIKTOK_OAUTH_UI_ENABLED,
  /**
   * Task 195 — IC-7.4 Snapchat MANUAL-SYNC UI build-time gate. Default OFF —
   * keeps the merged FE inert in production until backend activation. Gates
   * ONLY the sync trigger, not the connect card / account visibility.
   */
  ic7SnapchatSyncUiEnabled: IC7_SNAPCHAT_SYNC_UI_ENABLED,
};
